import { BrandMode } from "@Light/services/lightTypes";

// Keys as list so that we can pick out only known keys at run time.
const OVERRIDE_KEYS = [
  "companyName",
  "supportEmail",
  "icon",
  "logo",
  "brandMode",
] as const;
type OverrideKey = (typeof OVERRIDE_KEYS)[number];
export type Override = Partial<
  Record<Exclude<OverrideKey, "brandMode">, string> & { brandMode: BrandMode }
>;

const isBrandMode = (value: string): value is BrandMode =>
  (Object.values(BrandMode) as string[]).includes(value);

const OVERRIDE_PREFIX = "_override";

let override: Override | undefined;

export type FeatureFlagOverride = { enable: string[]; disable: string[] };

let featureFlagOverride: FeatureFlagOverride | undefined;

const parseFlagList = (param: string | null) =>
  (param ?? "")
    .split(",")
    .map((flag) => flag.trim())
    .filter(Boolean);

/**
 * Records the override from the `_override.<key>` query parameters. Should be
 * called immediately on startup, before the URL has a chance to change.
 */
export function initializeOverride() {
  const params = new URLSearchParams(window.location.search);
  for (const key of OVERRIDE_KEYS) {
    const param = params.get(`${OVERRIDE_PREFIX}.${key}`);
    if (typeof param !== "string") continue;
    if (key === "brandMode") {
      if (!isBrandMode(param)) continue;
      override = { ...override, brandMode: param };
    } else {
      override = { ...override, [key]: param };
    }
  }

  const enable = parseFlagList(params.get(`${OVERRIDE_PREFIX}.enableFlags`));
  const disable = parseFlagList(params.get(`${OVERRIDE_PREFIX}.disableFlags`));
  if (enable.length || disable.length) {
    featureFlagOverride = { enable, disable };
  }
}

/** Returns configuration overrides captured at initialization (if any were made.) */
export function getOverride() {
  return override;
}

// Writes the override params that `initializeOverride` reads back; empty lists omitted.
export function appendFeatureFlagOverride(
  url: URL,
  { enable, disable }: FeatureFlagOverride,
): void {
  if (enable.length) {
    url.searchParams.set(`${OVERRIDE_PREFIX}.enableFlags`, enable.join(","));
  }
  if (disable.length) {
    url.searchParams.set(`${OVERRIDE_PREFIX}.disableFlags`, disable.join(","));
  }
}

/** Applies the feature flags forced on or off at initialization (if any were.) */
export function applyFeatureFlagOverride(
  flags: string[] | null,
): string[] | null {
  if (!featureFlagOverride) return flags;
  const { enable, disable } = featureFlagOverride;
  const result = new Set([...(flags ?? []), ...enable]);
  for (const flag of disable) result.delete(flag);
  return [...result];
}
